import React from 'react';

import { BodyCopy } from '@xingternal/typography';
import { Row, Column } from '@xingternal/xing-grid';
import UserCardSkeleton from './Skeleton';
import ProfileInfo from '@xingternal/profile-info';
import Error from '@xingternal/pages-warning';
import { capitalizeString } from '@xingternal/pages-common';
import type * as Types from '@xingternal/pages-graphql-types';
import { ApolloError } from '@apollo/client';

import styles from './SocialProofList.css';

type SocialProofListProps = {
  title?: string;
  users: {
    xingId: Types.XingId;
  }[];
  loading?: boolean;
  error?: ApolloError;
  refetch: () => void;
  fetchMore: () => void;
  fetchMoreError: boolean;
};

const RenderSkeleton = ({ numberOfItems }: { numberOfItems: number }) => {
  const skeletons = [];

  for (let i = 0; i < numberOfItems; i++) {
    skeletons.push(
      <div className={styles.listItem}>
        <UserCardSkeleton />
      </div>
    );
  }

  return <>{skeletons.join()}</>;
};

const SocialProofList = ({
  title,
  users,
  loading,
  error,
  refetch,
  fetchMore,
  fetchMoreError,
}: SocialProofListProps) => (
  <Row>
    <Column
      className={styles.container}
      size={12}
      sizeDesktop={5}
      sizeTablet={5}
    >
      {title && (
        <BodyCopy
          lineHeight="dense"
          fontWeight="bold"
          size="large"
          className={styles.title}
        >
          {title}
        </BodyCopy>
      )}
      {users.length === 0 && loading && (
        <div className={styles.list}>
          <RenderSkeleton numberOfItems={2} />
        </div>
      )}
      {users.length !== 0 && (
        <ul className={styles.list} data-testid={'SOCIAL_PROOF_LIST'}>
          {users.map((user, index) => {
            const flag =
              user.xingId?.userFlags?.displayFlag &&
              user.xingId?.userFlags?.displayFlag !== 'BASIC' &&
              user.xingId?.userFlags?.displayFlag !== 'EXECUTIVE'
                ? {
                    size: 'small',
                    variant: user.xingId.userFlags.displayFlag.toLowerCase(),
                    children: capitalizeString(
                      user.xingId.userFlags.displayFlag
                    ),
                  }
                : null;

            return (
              <li
                key={index}
                className={styles.listItem}
                data-cy={'social-proof-contact'}
              >
                <ProfileInfo
                  key={user.xingId?.id}
                  profileUrl={
                    user?.xingId?.clickReasonProfileUrl?.profileUrl ||
                    `/profile/${user.xingId?.pageName}`
                  }
                  profileImage={{
                    size: 'small',
                    src: user.xingId?.profileImage?.[0]
                      ? user.xingId?.profileImage[0].url
                      : null,
                    profileName: 'user profile image',
                  }}
                  headline={{
                    size: 'small',
                    children: user.xingId?.displayName,
                  }}
                  flag={flag}
                  className={styles.profileInfo}
                  textBody={[
                    {
                      size: 'small',
                      children: user.xingId?.occupations?.[0]?.headline,
                    },
                  ]}
                  textMeta={[
                    {
                      size: 'small',
                      children: user.xingId?.occupations?.[0]?.subline,
                    },
                  ]}
                />
              </li>
            );
          })}
        </ul>
      )}
      {(error || fetchMoreError) && (
        <div data-testid={'errorContainer'}>
          <Error
            headerText="EP_ERROR_HEADER"
            bodyText="EP_ERROR_BODY"
            buttonText="EP_ERROR_RELOAD_CTA"
            onClick={fetchMoreError ? () => fetchMore() : () => refetch()}
          />
        </div>
      )}
    </Column>
  </Row>
);

export default SocialProofList;
